import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { Btn, PasswordField, PasswordRules, passwordRules } from "@/dashboard/ui";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!passwordRules.every((r) => r.test(password))) {
      setError("Please meet all the password requirements below.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.12)]">
          <Link to="/" className="flex items-center justify-center gap-2.5" data-testid="reset-brand">
            <BrandLogo />
            <span className="text-lg font-bold tracking-tight text-[#111111]">Savy Kerno</span>
          </Link>

          {!token ? (
            <div className="mt-8 text-center" data-testid="reset-invalid">
              <h1 className="text-xl font-extrabold tracking-tight text-[#111111]">Invalid reset link</h1>
              <p className="mt-2 text-sm text-neutral-500">This link is missing its token. Request a fresh one.</p>
              <Link to="/forgot-password" className="mt-6 inline-block text-sm font-semibold text-[#111111] underline underline-offset-4">
                Request a new link
              </Link>
            </div>
          ) : done ? (
            <div className="mt-8 text-center" data-testid="reset-success">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#111111] text-white">
                <CheckCircle2 size={20} />
              </span>
              <h1 className="mt-5 text-xl font-extrabold tracking-tight text-[#111111]">Password updated</h1>
              <p className="mt-2 text-sm leading-relaxed text-neutral-500">
                Your password has been changed and all existing sessions have been signed out.
              </p>
              <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-[#111111] underline underline-offset-4">
                Sign in with your new password
              </Link>
            </div>
          ) : (
            <>
              <h1 className="mt-6 text-center text-xl font-extrabold tracking-tight text-[#111111]">Choose a new password</h1>
              <p className="mt-1.5 text-center text-sm text-neutral-500">
                This link works once, for 30 minutes.
              </p>
              <form onSubmit={submit} className="mt-7 space-y-4" data-testid="reset-form">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-neutral-600">New password</label>
                  <PasswordField
                    testid="reset-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="14+ characters"
                  />
                </div>
                <PasswordRules password={password} />
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-neutral-600">Confirm new password</label>
                  <PasswordField
                    testid="reset-confirm-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat your new password"
                  />
                  {confirm && password !== confirm && (
                    <p data-testid="reset-confirm-mismatch" className="mt-1.5 text-xs text-red-600">
                      Passwords do not match.
                    </p>
                  )}
                </div>
                {error && (
                  <p data-testid="reset-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
                )}
                <Btn data-testid="reset-submit-button" type="submit" disabled={busy} className="w-full py-3">
                  {busy ? "Updating…" : "Update Password"}
                </Btn>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
